import React from 'react'
import { connect } from 'react-redux'
import { Button, Card, Popconfirm, Table, Tag, message } from 'antd'
import PageHeader from '@/components/PageHeader'
import Permission from '@/components/PermissionA5'
import { Search, SelectC } from '@/i2antdV5'
import { renderPhoneStr, renderTextStr } from '@/utils/renderUtils'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { fetchTuinaMemberList } from '@/redux/tuina/memberListSlice'
import { freezeTuinaMember, unfreezeTuinaMember } from '@/service/tuina'
import { TUINA_GENDER_OPTIONS } from '@/views/tuina/options'
import MemberModal from './share/MemberModal'
import MemberEntitlementDrawer from './share/MemberEntitlementDrawer'
import './Page.less'

const breadcrumbList = [
  { breadcrumbName: '按摩' },
  { breadcrumbName: '会员' }
]

/** 列表默认查询（仅页面侧使用，不写入 Redux initialState） */
const DEFAULT_TUINA_MEMBER_LIST_QUERY = {
  pageNumber: 1,
  pageSize: 20
}

const mergeTuinaMemberListQuery = (reduxQuery) =>
  reduxQuery && Object.keys(reduxQuery).length > 0 ? reduxQuery : DEFAULT_TUINA_MEMBER_LIST_QUERY

const STATUS_TEXT = {
  ACTIVE: '正常',
  FROZEN: '冻结'
}

class TuinaMembersPage extends React.Component {
  componentDidMount () {
    this.props.fetchJStoreList({ query: {}, dataType: 'optionTuina' })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载门店列表失败'))
    this.fetchList(DEFAULT_TUINA_MEMBER_LIST_QUERY)
  }

  fetchList = (formatQuery) => {
    this.props
      .fetchTuinaMemberList({ query: formatQuery })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载会员失败'))
  }

  refreshList = () => {
    this.fetchList(mergeTuinaMemberListQuery(this.props.memberList.query))
  }

  handleTableChange = (pagination) => {
    const query = mergeTuinaMemberListQuery(this.props.memberList.query)
    this.fetchList({
      ...query,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    })
  }

  handleModalSuccess = () => {
    this.refreshList()
  }

  handleToggleFreeze = async (record) => {
    const isFrozen = record.status === 'FROZEN'
    try {
      if (isFrozen) {
        await unfreezeTuinaMember(record.id)
      } else {
        await freezeTuinaMember(record.id)
      }
    } catch (e) {
      return
    }
    message.success(isFrozen ? '已解冻' : '已冻结')
    this.refreshList()
  }

  storeName = (storeId) => {
    const options = this.props.getJStoreList.data
    if (!Array.isArray(options)) return storeId
    const row = options.find((item) => String(item.id) === String(storeId))
    return row ? row.value : storeId
  }

  getColumns = () => [
    {
      title: '门店',
      dataIndex: 'storeId',
      width: 140,
      render: (v) => renderTextStr(this.storeName(v))
    },
    {
      title: '会员号',
      dataIndex: 'memberNo',
      width: 128,
      render: (v) => renderTextStr(v)
    },
    {
      title: '姓名',
      dataIndex: 'name',
      width: 120,
      render: (v) => renderTextStr(v)
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      width: 140,
      render: (v) => renderPhoneStr(v)
    },
    {
      title: '性别',
      dataIndex: 'gender',
      width: 72,
      render: (v) => {
        const row = TUINA_GENDER_OPTIONS.find((item) => item.id === v || item.id === Number(v))
        return renderTextStr(row && row.value)
      }
    },
    {
      title: '生日',
      dataIndex: 'birthday',
      width: 120,
      render: (v) => renderTextStr(v)
    },
    {
      title: '是否绑定小程序',
      dataIndex: 'linkedCustUser',
      width: 140,
      render: (v) => (
        v === true
          ? renderTextStr('已绑定')
          : <span className="tuina-member-unbound">未绑定</span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 88,
      render: (v) => {
        const text = STATUS_TEXT[v] || v
        if (!text) return renderTextStr(text)
        const color = v === 'FROZEN' ? 'default' : 'success'
        return <Tag color={color}>{text}</Tag>
      }
    },
    {
      title: '入会时间',
      dataIndex: 'joinedAt',
      width: 180,
      render: (v) => renderTextStr(v)
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      render: (_, record) => {
        const isFrozen = record.status === 'FROZEN'
        const actionText = isFrozen ? '解冻' : '冻结'
        return (
          <div className="tuina-member-actions">
            <MemberEntitlementDrawer record={record}>
              <Button type="link" className="tuina-member-action-plain" aria-label={`${record.name}套餐详情`}>套餐详情</Button>
            </MemberEntitlementDrawer>
            <Permission rootPath="tuinaPage">
              <span pathitem={['members']} checkarray={['buttonCodes']}>
              <MemberModal actType="edit" record={record} onSuccess={this.handleModalSuccess}>
                <Button type="link" className="tuina-member-action-plain" aria-label={`改 ${record.name}`}>编辑</Button>
              </MemberModal>
              <Popconfirm
                title={`确认${actionText}该会员？`}
                description={record.name}
                okText={actionText}
                cancelText="取消"
                okButtonProps={{ danger: !isFrozen, size: 'large' }}
                cancelButtonProps={{ size: 'large' }}
                onConfirm={() => this.handleToggleFreeze(record)}
              >
                <Button type="link" danger={!isFrozen} aria-label={`${actionText} ${record.name}`}>
                  {actionText}
                </Button>
              </Popconfirm>
              </span>
            </Permission>
          </div>
        )
      }
    }
  ]

  render () {
    const { memberList, getJStoreList } = this.props
    const { fetching, query: reduxQuery, data } = memberList
    const query = mergeTuinaMemberListQuery(reduxQuery)
    const list = Array.isArray(data.list) ? data.list : []
    const totalRow = data.totalRow || 0
    const jStoreOptions = getJStoreList.data

    return (
      <div>
        <PageHeader title="会员" breadcrumbList={breadcrumbList} />
        <div className="i2-container">
          <Card className="i2-card-page">
            <section className="i2-app-toolbar tuina-member-toolbar">
              <div className="toolbar-filters">
                <SelectC
                  label="门店"
                  query={query}
                  searchKey="storeId"
                  options={jStoreOptions}
                  action={this.fetchList}
                  placeholder="全部"
                  modalProps={{ allowClear: true }}
                />
                <Search
                  label="关键字"
                  query={query}
                  searchKey="keyword"
                  action={this.fetchList}
                  modalProps={{ allowClear: true, placeholder: '姓名/手机/会员号' }}
                />
              </div>
              <div className="toolbar-btns">
                <Permission rootPath="tuinaPage">
                  <span pathitem={['members']} checkarray={['buttonCodes']}>
                    <MemberModal actType="create" onSuccess={this.handleModalSuccess}>
                      <Button type="primary" size="large">新增</Button>
                    </MemberModal>
                  </span>
                </Permission>
              </div>
            </section>
            <Table
              className="tuina-member-table"
              rowKey={(r) => String(r.id)}
              columns={this.getColumns()}
              dataSource={list}
              loading={fetching}
              onChange={this.handleTableChange}
              pagination={{
                current: query.pageNumber,
                pageSize: query.pageSize,
                total: totalRow,
                showSizeChanger: true,
                showTotal: (t) => `共 ${t} 条`,
                pageSizeOptions: ['10', '20', '50', '100']
              }}
            />
          </Card>
        </div>
      </div>
    )
  }
}

const mapStateToProps = (state) => ({
  memberList: state.tuina.memberList,
  getJStoreList: state.components.getJStoreList
})

const mapDispatchToProps = {
  fetchTuinaMemberList,
  fetchJStoreList
}

export default connect(mapStateToProps, mapDispatchToProps)(TuinaMembersPage)
